import {
  ArrowRight,
  CalendarClock,
  Check,
  ImagePlus,
  MessageSquareQuote,
  PencilLine,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link, type MetaFunction } from "react-router";

import { Button } from "@/ui/button";

import { PricingPlans } from "./pricing-plans";

export const meta: MetaFunction = () => {
  return [
    { title: `Pricing | ${publicEnv.siteTitle}` },
    {
      name: "description",
      content:
        "Choose a family subscription for Anecdote FYI's private handwriting practice and family keepsakes.",
    },
  ];
};

// Explain the family subscription and the product boundary before signup.
//
export default function Pricing() {
  const trialDays = publicEnv.stripeTrialDays;

  return (
    <>
      <section className="grid items-end gap-10 py-18 sm:py-24 lg:grid-cols-[1fr_20rem] lg:py-28">
        <div className="max-w-3xl">
          <p className="text-primary font-mono text-xs font-semibold tracking-[0.16em] uppercase">
            Simple family pricing
          </p>
          <h1 className="font-display mt-5 text-5xl leading-[0.98] font-semibold tracking-[-0.035em] sm:text-6xl">
            One family.
            <br />
            Every handwritten story.
          </h1>
          <p className="text-muted-foreground mt-6 max-w-2xl text-lg leading-8">
            One subscription lets guardians send private photo prompts and gives
            each child a place to practise writing on paper. Keep the original
            handwriting, compare pages through the years, and print the stories
            your family wants to revisit.
          </p>
          <Button asChild className="mt-8 h-12 px-6 text-base" size="lg">
            <a href="#plans">
              Compare plans
              <ArrowRight />
            </a>
          </Button>
        </div>

        <aside className="bg-accent/70 rounded-2xl border p-6">
          <CalendarClock className="text-primary size-5" />
          <p className="font-display mt-4 text-2xl font-semibold">
            {trialDays > 0 ? `${trialDays} days free` : "Start when ready"}
          </p>
          <p className="text-muted-foreground mt-2 text-sm leading-6">
            {trialDays > 0
              ? "Try the complete family workspace before your first payment. No card is required to begin."
              : "Choose the plan that fits your family and manage it from Billing."}
          </p>
        </aside>
      </section>

      <section
        aria-labelledby="plans-title"
        className="scroll-mt-20 pb-24 sm:pb-32"
        id="plans"
      >
        <div className="grid items-end gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="text-primary font-mono text-xs font-semibold tracking-[0.16em] uppercase">
              Choose your plan
            </p>
            <h2
              className="font-display mt-4 text-4xl leading-tight font-semibold sm:text-5xl"
              id="plans-title"
            >
              The whole workspace is included.
            </h2>
          </div>
          <p className="text-muted-foreground max-w-xl leading-7 lg:justify-self-end">
            Every plan includes writing sessions, original handwriting crops,
            saved drafts, family review, page comparisons, printable worksheets,
            PDF keepsakes, and a portable family archive.
          </p>
        </div>

        <PricingPlans className="mt-12" />
        <p className="text-muted-foreground mt-4 text-center text-xs">
          Payments are securely handled by Stripe. The displayed terms come from
          the active plan catalog.
        </p>
      </section>

      <section className="bg-ink text-ink-foreground anecdote-thread overflow-hidden rounded-3xl px-6 py-12 sm:px-10 sm:py-16 lg:px-16">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="text-ink-muted font-mono text-xs tracking-[0.16em] uppercase">
              Included in every plan
            </p>
            <h2 className="font-display mt-4 text-4xl leading-tight font-semibold sm:text-5xl">
              Room to practise. Memories to keep.
            </h2>
          </div>

          <div className="grid gap-x-8 gap-y-7 sm:grid-cols-2">
            <IncludedFeature icon={ImagePlus} title="Private photo prompts">
              Choose from a device or the Dropbox Chooser, add an optional
              question, and send only to selected children.
            </IncludedFeature>
            <IncludedFeature
              icon={MessageSquareQuote}
              title="Handwriting practice"
            >
              Choose a passage, write it on paper, and photograph the result.
              Speaking, grammar help, and reading help are optional.
            </IncludedFeature>
            <IncludedFeature icon={UsersRound} title="The whole family">
              Guardians share access while each child sees only the photos sent
              to their profile.
            </IncludedFeature>
            <IncludedFeature icon={PencilLine} title="Stories worth keeping">
              Keep each new handwritten page, compare different writing dates,
              and download one story per PDF page or the whole family as a ZIP.
            </IncludedFeature>
          </div>
        </div>
      </section>

      <section className="grid gap-12 py-24 sm:py-32 lg:grid-cols-[0.75fr_1.25fr]">
        <div>
          <p className="text-primary font-mono text-xs font-semibold tracking-[0.16em] uppercase">
            Pricing FAQ
          </p>
          <h2 className="font-display mt-4 text-4xl leading-tight font-semibold sm:text-5xl">
            The practical details.
          </h2>
        </div>

        <div className="divide-y border-y">
          <PricingQuestion question="Does one subscription cover the family?">
            Yes. Billing belongs to the family workspace, so its guardians and
            invited children are included rather than charged individually.
          </PricingQuestion>
          <PricingQuestion question="How much can our family keep?">
            Each family has 1 GB for handwriting originals, print copies,
            drafts, and history, plus 500 MB for family photos. You can have 250
            waiting photo prompts, 12 active children, and 6 active guardians.
            Completed stories do not use the waiting-prompt allowance. Storage
            usage is visible in Family, and saved work remains readable if you
            reach a limit.
          </PricingQuestion>
          <PricingQuestion question="Does Anecdote FYI connect to my Dropbox?">
            No. Dropbox&apos;s own Chooser returns only the files you explicitly
            select. Anecdote FYI downloads those temporary selections in your
            browser, removes camera metadata, and stores a normalized private
            copy for the chosen child prompt.
          </PricingQuestion>
          {trialDays > 0 && (
            <PricingQuestion question="Do I need a card for the free trial?">
              No. You can start the {trialDays}-day trial without a payment
              method. Add one in Billing before the trial ends to continue
              without interruption.
            </PricingQuestion>
          )}
          <PricingQuestion question="Can I change plans or cancel?">
            Yes. An adult can change the available plan, update the payment
            method, or manage cancellation from Billing. Existing handwriting,
            comparisons, PDF downloads, family export, and owner deletion stay
            available after paid access ends. New writing sessions require an
            active plan.
          </PricingQuestion>
        </div>
      </section>

      <section className="bg-accent/70 mb-24 rounded-3xl px-6 py-14 text-center sm:px-10 sm:py-18">
        <ShieldCheck className="text-primary mx-auto size-6" />
        <p className="mt-5 font-mono text-xs font-semibold tracking-[0.16em] uppercase">
          One photo is enough to begin
        </p>
        <h2 className="font-display mx-auto mt-4 text-4xl font-semibold sm:text-5xl">
          Start with one photograph.
        </h2>
        <Button asChild className="mt-8 h-12 px-6 text-base" size="lg">
          <Link to="/login?redirectTo=%2Fbilling">
            {trialDays > 0
              ? `Start your free ${trialDays}-day trial`
              : "Create a family workspace"}
            <ArrowRight />
          </Link>
        </Button>
      </section>
    </>
  );
}

interface IncludedFeatureProps {
  children: ReactNode;
  icon: typeof Check;
  title: string;
}

// Summarize one capability included in every subscription plan.
//
function IncludedFeature({
  children,
  icon: Icon,
  title,
}: IncludedFeatureProps) {
  return (
    <article>
      <Icon className="text-ink-accent size-5" />
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="text-ink-muted mt-2 text-sm leading-6">{children}</p>
    </article>
  );
}

interface PricingQuestionProps {
  children: ReactNode;
  question: string;
}

// Reveal pricing details on demand while keeping the page easy to scan.
//
function PricingQuestion({ children, question }: PricingQuestionProps) {
  return (
    <details className="group py-5">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium">
        {question}
        <span className="text-primary text-xl transition-transform group-open:rotate-45">
          +
        </span>
      </summary>
      <div className="text-muted-foreground max-w-2xl pt-3 text-sm leading-7">
        {children}
      </div>
    </details>
  );
}
